import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { Reveal } from "../components/motion";
import { GALLERY, IMG } from "../lib/site";

const FILTERS = [
  "All",
  "Loft Conversions",
  "Bathrooms",
  "Kitchens",
  "Full Refurbishments",
  "Extensions",
  "New Builds",
];

const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.cat === filter)),
    [filter]
  );

  return (
    <>
      <SEO
        title="Project Gallery | Esskay Homes — Recent Work Across the South East"
        description="Browse recent Esskay Homes projects: loft conversions, bathrooms, kitchens, extensions, refurbishments and new builds across London & the South East."
        path="/gallery"
      />

      <PageHero
        eyebrow="Project Gallery"
        title={["Recent work,", <>built to <em className="italic text-gold">last</em>.</>]}
        sub="A selection of projects across London & the South East. Every image is a finished space — surveyed, built and handed over by our own team."
        image={IMG.living1}
        crumbs={["Gallery"]}
      />

      <section className="container-ek py-20 lg:py-24" data-testid="gallery-section">
        <Reveal>
          <div className="flex flex-wrap gap-2.5" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`border px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] transition-all duration-300 ${
                  filter === f
                    ? "border-gold bg-gold text-navy"
                    : "border-white/15 text-white/70 hover:border-gold/50 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 columns-1 gap-4 sm:columns-2 lg:columns-3">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                key={g.title}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: (i % 6) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="corner-frame group relative mb-4 break-inside-avoid overflow-hidden"
                data-testid={`gallery-item-${i}`}
              >
                <button
                  data-testid={`gallery-open-${i}`}
                  onClick={() => setSelected(g)}
                  className="block w-full cursor-pointer"
                  aria-label={`View ${g.title}`}
                >
                  <img
                    src={g.img}
                    alt={g.title}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <figcaption className="absolute inset-x-0 bottom-0 translate-y-3 p-6 text-left opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{g.cat}</p>
                    <p className="mt-1.5 font-serif text-xl text-white">{g.title}</p>
                  </figcaption>
                </button>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-16 text-center">
          <p className="text-sm text-mist">Like what you see? Your project could be next.</p>
          <Link to="/contact" data-testid="gallery-cta" className="btn-gold group mt-6">
            Start Your Project
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/95 p-6 backdrop-blur-xl"
            onClick={() => setSelected(null)}
          >
            <motion.figure
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.img}
                alt={selected.title}
                className="max-h-[82vh] w-auto max-w-[90vw] object-contain"
              />
              <figcaption className="mt-4 flex items-baseline justify-between gap-6">
                <span className="font-serif text-xl text-white">{selected.title}</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-gold">
                  {selected.cat}
                </span>
              </figcaption>
              <button
                data-testid="gallery-lightbox-close"
                onClick={() => setSelected(null)}
                aria-label="Close image"
                className="absolute -top-3 right-0 flex h-11 w-11 -translate-y-full items-center justify-center border border-white/20 bg-navy/80 text-white transition-colors hover:border-gold hover:text-gold"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <CTABanner />
    </>
  );
};

export default Gallery;
